import React, {KeyboardEvent, ReactElement, ReactNode} from "react";

import algoliasearch from "algoliasearch/lite";
import {debounce} from "throttle-debounce";
import Dotdotdot from "react-dotdotdot";

import {GetSourcesParams, createAutocomplete} from "@algolia/autocomplete-core";

import {getAlgoliaResults} from "@algolia/autocomplete-preset-algolia";

import Label from "../label/Label";
import ErrorMessage from "../error-message/ErrorMessage";
import DropdownMenu from "../../dropdown-menu/DropdownMenu";
import Icon from "../../icon/Icon";

import translate from "/src/utils/translate";
import {css} from "/src/utils/utils";
import {FormGroupBase} from "/src/types";
import KEY from "/src/constants/keyboard-keydown-key";
import ChevronRightIcon from "/src/svg/chevron-right.svg";

import * as styles from "./auto-complete.module.scss";
import {OnStateChangeProps} from "@algolia/autocomplete-js";

const ALGOLIA_API_KEY = process.env.GATSBY_ALGOLIA_AUTOCOMPLETE_READ_KEY;

const ALGOLIA_APP_ID = process.env.GATSBY_ALGOLIA_APP_ID || "";

const algoliaClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_API_KEY || "");

export interface AutoCompleteDropdownOption {
  label: string;
  value: string;
  id?: string;
  disabled?: boolean;
  // eslint-disable-next-line camelcase
  __autocomplete_id: number;
  name?: string;
  [x: string | number]: any; // any other props
}

interface AutoCompleteProps extends FormGroupBase {
  placeholder?: string;
  onChange: (val: AutoCompleteDropdownOption | null) => unknown;
  onSearch?: (str: string) => unknown;
  options?: Array<AutoCompleteDropdownOption>;
  transparentBg?: boolean;
  disableError?: boolean;
  algoliaIndices?: string;
  algoliaFilter?: Record<any, any>;
  customItemRenderer?: (item: any) => ReactNode;

  hideDropdownButton?: boolean;
  minLettersForSearch?: number;
}

const AutoComplete = (props: AutoCompleteProps): ReactElement => {
  const {
    placeholder = "",
    label = "",
    required = false,
    name = "",
    value = "",
    errorMessage = "",
    status,
    disabled = false,
    onChange,
    onSearch,
    options = [],
    darkMode = false,
    transparentBg = false,
    disableError = false,
    algoliaIndices,
    algoliaFilter,
    customItemRenderer,

    hideDropdownButton = false,
    minLettersForSearch = 0,
  } = props;

  const [selectedValue, setSelectedValue] = React.useState<string>(value);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const prevSelectedValue = React.useRef<string>(value);

  const [focusedListItem, setFocusedListItem] = React.useState<number>(-1);
  const [autocompleteState, setAutocompleteState] = React.useState<
    Record<any, any>
  >({});
  const [defaultLabelValue, setDefaultLabelValue] = React.useState<
    string | null
  >(null);

  // ref: https://github.com/alphagov/accessible-autocomplete/blob/main/src/status.js
  const [assistiveStatusUpdate, setAssistiveStatusUpdate] = React.useState({
    debounced: false,
    silenced: false,
    bumped: false,
  });

  const $root = React.useRef<null | HTMLDivElement>(null);
  const $input = React.useRef<null | HTMLInputElement>(null);
  const dataFetchRetry = React.useRef<number>(0);

  const isValid = status === "success";
  const isInvalid = status === "error";

  const labelId = `${name}_label`;
  const triggerId = `${name}_trigger`;
  const itemListId = `${name}_list`;
  const inputId = `${name}_input`;

  // whether we should forced bottom anchor on
  // dropdown component
  // on touch screen device, we should just fixed
  // the menu anchoring at
  // the bottom, to fixed erratic dropdown position
  // on input focus, as the keyboard show up
  // ref: https://mui.com/material-ui/react-autocomplete/#combo-box for behaviour
  const isTouchDevice =
    typeof window !== "undefined" &&
    window.matchMedia("(any-pointer:coarse)").matches;

  const filteredListOptions: Array<AutoCompleteDropdownOption> = (
    autocompleteState.collections || []
  )
    .map((collection: Record<any, any>) => {
      const {items} = collection;

      return items || [];
    })
    .reduce((acc: any, items: any) => {
      return [...acc, ...items];
    }, []);

  if (filteredListOptions.length === 0) {
    // append no results
    filteredListOptions.push({
      label: translate("No results found"),
      value: "-",
      disabled: true,
      // eslint-disable-next-line camelcase
      __autocomplete_id: 0,
    });
  }

  /**
   * Returns label of option that matches provided value.
   * @param {string} val
   * @returns {string|null}
   */
  const getLabelByValue = React.useCallback(
    (val?: string): string => {
      if (!options || val == null || autocompleteState.collections == null) {
        return val || "";
      }

      const temp: Array<AutoCompleteDropdownOption> =
        filteredListOptions.filter(
          item => item.value === val || item.id === val
        );

      if (temp.length > 0) {
        return temp[0].name != null ? temp[0].name : temp[0].label;
      }

      return val;
    },
    [autocompleteState.collections, filteredListOptions, options]
  );

  const debounceSearchQuery = debounce(
    50,
    () => {
      if ($input.current) {
        const tempVal = $input.current.value;
        autocomplete.setQuery(tempVal || " ");
        autocomplete.setIsOpen(true);
        autocomplete.refresh();
      }
    },
    {atBegin: false}
  );

  // NOTE: we only support simple OR filter condition
  const constructsAlgoliaFilter = React.useCallback(() => {
    if (algoliaFilter != null) {
      return Object.keys(algoliaFilter)
        .map(key => {
          return `(${key}: ${String(algoliaFilter[key])})`;
        })
        .join(" OR ");
    }

    return "";
  }, [algoliaFilter]);

  const autocomplete = React.useMemo(() => {
    return createAutocomplete({
      id: `${name}-algolia-autocomplete-instance`,
      onStateChange({state}: OnStateChangeProps<AutoCompleteDropdownOption>) {
        // (2) Synchronize the Autocomplete state with the React state.
        setAutocompleteState(state);
      },
      getSources() {
        // TODO: consider reshaping data to avoid duplicate data
        // ref: https://www.algolia.com/doc/ui-libraries/autocomplete/guides/reshaping-sources/
        const sources = [
          // (1) load static data
          {
            // it's important to have unique sourceId, as algolia
            // will cache the data. if we have multiple autocomplete
            // component in a page, this will resolve the issue
            // of data loaded on mount, will be unique in each component
            sourceId: `static-${name}`,
            getItems({query}: GetSourcesParams<any>) {
              const staticOptions = options || [];
              if (!query) {
                return staticOptions;
              }

              return staticOptions.filter(
                item =>
                  item.label.toLowerCase().includes(query.toLowerCase()) ||
                  item.value.toLowerCase().includes(query.toLowerCase())
              );
            },
            getItemInputValue({item}: any) {
              return item.query;
            },
            getItemUrl({item}: any) {
              return item.url;
            },
          },
        ];

        // adding data source from algolia
        if (algoliaIndices != null) {
          sources.push({
            // it's important to have unique sourceId, as algolia
            // will cache the data. if we have multiple autocomplete
            // component in a page, this will resolve the issue
            // of data loaded on mount, will be unique in each component
            sourceId: `${algoliaIndices}-${name}`,
            getItemInputValue({item}: any) {
              return item.query;
            },
            getItemUrl({item}: any) {
              return item.url;
            },
            getItems({query}: GetSourcesParams<any>) {
              return getAlgoliaResults({
                searchClient: algoliaClient,
                queries: [
                  {
                    indexName: algoliaIndices,
                    query: query,
                    params: {
                      filters: constructsAlgoliaFilter(),
                    },
                  },
                ],
              }) as any;
            },
          });
        }

        return sources;
      },
    });
  }, [algoliaIndices, constructsAlgoliaFilter, name, options]);

  const hasMinLettersForSearch = React.useCallback(() => {
    if ($input.current) {
      return $input.current.value.length >= minLettersForSearch;
    }

    return false;
  }, [minLettersForSearch]);

  const onInputFocus = () => {
    if (!isOpen && hasMinLettersForSearch()) {
      setIsOpen(true);
      // on open, try to get the search query
      debounceSearchQuery();
    }

    // for safari fix that works for all browser.
    // wait for the next paint before selecting all text input
    requestAnimationFrame(() => {
      if ($input.current) {
        $input.current.select();
      }
    });
  };

  const onInputChange = () => {
    if (!isOpen && hasMinLettersForSearch()) {
      setIsOpen(true);
    }

    debounceSearchQuery();

    if (onChange) {
      onChange(null);
    }

    if ($input.current) {
      // by default we update selected value on input change
      setSelectedValue($input.current.value);

      if (onSearch) {
        onSearch($input.current.value);
      }
    }

    setAssistiveStatusUpdate({
      ...assistiveStatusUpdate,
      debounced: false,
    });
  };

  const onInputKeydown = (_e: KeyboardEvent) => {
    switch (_e.key) {
      case KEY.ENTER:
        _e.preventDefault();
        if (focusedListItem !== -1) {
          selectValue(filteredListOptions[focusedListItem]);
        }
        break;
      case KEY.TAB:
      case KEY.ESC:
        setIsOpen(false);
        setFocusedListItem(-1);
        break;
      case KEY.UP:
        {
          _e.preventDefault();
          const tempIdx = focusedListItem - 1;

          focusListItemByIdx(tempIdx);

          setIsOpen(true);
        }
        break;
      case KEY.DOWN:
        {
          _e.preventDefault();
          const tempIdx = focusedListItem + 1;

          focusListItemByIdx(tempIdx);

          setIsOpen(true);
        }
        break;
      default:
        break;
    }
  };

  const focusListItemByIdx = (idx: number) => {
    if (idx > -1 && idx < filteredListOptions.length) {
      setFocusedListItem(idx);
    }
  };

  const selectValue = (val: AutoCompleteDropdownOption) => {
    setIsOpen(false);

    if (val.disabled === true) {
      return;
    }

    const derivedValue = val.id != null ? val.id : val.name;

    if (derivedValue) {
      setSelectedValue(derivedValue);
    }

    if ($input.current) {
      $input.current.value = val.name != null ? val.name : val.label;
    }

    if (onChange) {
      onChange(val);
    }
  };

  const onListItemClick = (val: AutoCompleteDropdownOption) => {
    selectValue(val);
  };

  const onListItemEnter = (idx: number) => {
    setFocusedListItem(idx);
  };

  const onListItemLeave = () => {
    setFocusedListItem(-1);
  };

  React.useEffect(() => {
    if (!isOpen) {
      setFocusedListItem(-1);
    } else {
      // we don't want to perform search query if search value is the same?
      if (
        $input.current &&
        prevSelectedValue.current !== $input.current.value
      ) {
        debounceSearchQuery();
        prevSelectedValue.current = $input.current.value;
      }
    }

    return () => {}; // satisfies eslint's consistent-return rule
  }, [debounceSearchQuery, isOpen]);

  // wait till initial algolia data fetched. then we initial grab label value
  React.useEffect(() => {
    // why this needed? for some reason
    // data got mixed up
    // so adding a check if collections contains
    // the correct source id, and if not, re-fetch
    // will fix the issue
    // GROW-2670

    // check if collections data contains the correct sourceId
    // otherwise, try to re-fetch data
    if (
      autocompleteState.collections != null &&
      autocompleteState.collections.length &&
      algoliaIndices != null &&
      dataFetchRetry.current < 4
    ) {
      const check = autocompleteState.collections.filter(
        (item: any) => item.source.sourceId === `${algoliaIndices}-${name}`
      );

      if (check.length === 0) {
        debounceSearchQuery();
        dataFetchRetry.current += 1;
        return;
      }
    }

    if (
      defaultLabelValue === null &&
      autocompleteState.collections != null &&
      autocompleteState.collections.length
    ) {
      setDefaultLabelValue(getLabelByValue(selectedValue));
    }
  }, [
    algoliaIndices,
    autocompleteState,
    debounceSearchQuery,
    defaultLabelValue,
    getLabelByValue,
    name,
    selectedValue,
  ]);

  const totalRes = filteredListOptions.filter(item => item.disabled !== true);
  const assistiveId = `${name}__assistiveHint`;

  const words = {
    result: totalRes.length === 1 ? "result" : "results",
    is: totalRes.length === 1 ? "is" : "are",
  };

  let atomicHint = "";
  const totalHint =
    totalRes.length === 0
      ? "No results found"
      : `${totalRes.length} ${words.result} ${words.is} available.`;

  if (
    focusedListItem >= 0 &&
    totalRes.length > 0 &&
    filteredListOptions[focusedListItem]
  ) {
    atomicHint = `${filteredListOptions[focusedListItem].name || ""} ${
      focusedListItem + 1
    } of ${totalRes.length} is highlighted.`;
  }

  return (
    <div
      className={css(
        styles.autoComplete,
        darkMode ? styles.autoCompleteDark : "",
        transparentBg ? styles.autoCompleteTransparent : "",
        isOpen ? styles.autoCompleteOpen : "",
        isOpen ? styles.autoCompleteFocus : "",
        isInvalid ? styles.autoCompleteError : "",
        isValid ? styles.autoCompleteSuccess : "",
        disabled ? styles.autoCompleteDisabled : ""
      )}
    >
      <Label
        darkMode={darkMode}
        disabled={disabled}
        label={label}
        required={required}
        id={labelId}
        ariaHidden={label === ""}
      />

      <div ref={$root} className={css(styles.autoComplete__el)}>
        <div className={css(styles.autoComplete__inputWrapper)}>
          <input type="hidden" name={name} value={selectedValue} />
          <div
            className={css(styles.autoComplete__hint)}
            role="status"
            aria-atomic="true"
            aria-live="polite"
          >
            {totalHint}
            {!assistiveStatusUpdate.silenced &&
            assistiveStatusUpdate.debounced &&
            assistiveStatusUpdate.bumped
              ? totalHint
              : ""}
          </div>
          <div
            className={css(styles.autoComplete__hint)}
            role="status"
            aria-atomic="true"
            aria-live="polite"
          >
            {atomicHint}
            {!assistiveStatusUpdate.silenced &&
            assistiveStatusUpdate.debounced &&
            !assistiveStatusUpdate.bumped
              ? atomicHint
              : ""}
          </div>
          <input
            autoComplete="off"
            value={getLabelByValue(selectedValue)}
            disabled={disabled}
            type="text"
            id={inputId}
            ref={$input}
            onChange={onInputChange}
            onKeyDown={onInputKeydown}
            onFocus={onInputFocus}
            placeholder={placeholder}
            className={css(styles.autoComplete__input)}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={isOpen}
            aria-controls={itemListId}
            aria-describedby={assistiveId}
          />
          {!hideDropdownButton ? (
            <button
              className={css("p", styles.autoComplete__button)}
              tabIndex={-1}
              type="button"
              aria-expanded={isOpen}
              aria-controls={itemListId}
              id={triggerId}
              onClick={() => {
                setIsOpen(!isOpen);
              }}
              aria-label={translate(isOpen ? "collapse" : "expand")}
            >
              <Icon
                svg={ChevronRightIcon}
                role="presentation"
                aria-hidden="true"
                classes={styles.autoComplete__icon}
              />
            </button>
          ) : undefined}
        </div>

        <DropdownMenu
          bottomStyles={{
            borderRadius: "0 0 4px 4px",
            borderBottom: "2px solid",
            borderBottomColor: "inherit",
          }}
          classes={styles.autoComplete__dropdown}
          forceBottomAnchor={isTouchDevice}
          maxHeight={344}
          offsetY={-4}
          open={isOpen}
          parent={$root}
          restrictWidth
          toggle={(val: boolean) => {
            // if input is in focus, and val is false, do nothing
            if (document.activeElement === $input.current && !val) {
              return;
            }
            setIsOpen(val);
          }}
          topStyles={{borderRadius: "4px 4px 0 0", borderTop: "2px solid"}}
        >
          <ul
            className={css("p", styles.autoComplete__list)}
            id={itemListId}
            tabIndex={-1}
            role="listbox"
            aria-activedescendant={selectedValue}
            aria-hidden={!isOpen}
            aria-labelledby={labelId}
          >
            {filteredListOptions.map(
              (option: AutoCompleteDropdownOption, i: number) => {
                const derivedValue =
                  option.id != null ? option.id : option.value;
                const selected = selectedValue === derivedValue;
                const listIsFocus = i === focusedListItem;

                return (
                  <li
                    onFocus={() => onListItemEnter(i)}
                    onBlur={onListItemLeave}
                    // eslint-disable-next-line no-underscore-dangle
                    key={`${name}-${option.__autocomplete_id}-key`}
                    onClick={() => onListItemClick(option)}
                    className={css(
                      styles.autoComplete__item,
                      listIsFocus ? styles.autoComplete__itemFocus : "",
                      selected ? styles.autoComplete__itemSelected : ""
                    )}
                    id={option.value || option.id}
                    role="option"
                    tabIndex={-1}
                    aria-label={option.label || option.name}
                    aria-selected={listIsFocus}
                    aria-setsize={totalRes.length}
                  >
                    {customItemRenderer ? (
                      customItemRenderer(option)
                    ) : (
                      <Dotdotdot clamp={2} useNativeClamp>
                        {option.label || option.name}
                      </Dotdotdot>
                    )}
                  </li>
                );
              }
            )}
          </ul>
          <span id={assistiveId} className={css(styles.autoComplete__hint)}>
            {translate(
              "When autocomplete results are available use up and down arrows to review and enter to select. Touch device users, explore by touch or with swipe gestures."
            )}
          </span>
        </DropdownMenu>
      </div>
      {!disableError && (
        <ErrorMessage message={errorMessage} visible={isInvalid} />
      )}
    </div>
  );
};

export default AutoComplete;
